import { Fragment, useState, useEffect, useCallback } from "react";
import {
    RefreshCw, Loader2, GitBranch, ChevronRight, ChevronDown, ExternalLink,
} from "lucide-react";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import useApiMethods from "../../lib/useApiMethods";
import { AD_ATTRIBUTION_REPORT_URL } from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";
import { usePersistedState } from "../../hooks/usePersistedState";
import { cn } from "../../utils/tailwindUtils";
import {
    AdFilterBar, AttributionBanner, EmptyState, StatTile, money, num, pct,
    resolveDateRange,
    StatusPill, isDelivering,
    StatusFilterSelect,
} from "./adReportKit";

/** Four proportional bars: leads → contacted → demo → won, for one node. */
const OutcomeBar = ({ row }) => {
    const base = row.leads || 1;
    const steps = [
        { k: "leads", cls: "bg-blue-500" },
        { k: "contacted", cls: "bg-indigo-500" },
        { k: "demos", cls: "bg-violet-500" },
        { k: "won", cls: "bg-emerald-500" },
    ];
    return (
        <div className="flex items-center gap-1 min-w-[110px]">
            {steps.map((s) => (
                <div key={s.k} className="flex-1" title={`${s.k}: ${row[s.k] || 0}`}>
                    <div className="h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                        <div className={s.cls}
                            style={{ width: `${Math.min(((row[s.k] || 0) * 100) / base, 100)}%`, height: "100%" }} />
                    </div>
                    <p className="text-[9px] text-muted-foreground text-center mt-0.5 tabular-nums">
                        {row[s.k] || 0}
                    </p>
                </div>
            ))}
        </div>
    );
};

const Cells = ({ row }) => (
    <>
        <td className="px-2 py-2 text-right font-semibold text-foreground tabular-nums">{num(row.leads)}</td>
        <td className="px-2 py-2 text-right text-muted-foreground tabular-nums">{num(row.quality)}</td>
        <td className="px-2 py-2 text-right text-muted-foreground tabular-nums">{num(row.demos)}</td>
        <td className="px-2 py-2 text-right text-foreground font-medium tabular-nums">{num(row.won)}</td>
        <td className="px-2 py-2 text-right tabular-nums">
            <span className={cn("font-semibold",
                row.win_rate == null ? "text-muted-foreground"
                    : row.win_rate >= 10 ? "text-emerald-600 dark:text-emerald-400"
                        : "text-foreground")}>
                {pct(row.win_rate, 1)}
            </span>
        </td>
        <td className="px-2 py-2"><OutcomeBar row={row} /></td>
        <td className="px-2 py-2 text-right text-muted-foreground tabular-nums">{money(row.spend)}</td>
        <td className="px-2 py-2 text-right text-amber-700 dark:text-amber-400 tabular-nums">{money(row.cpl)}</td>
        <td className="px-2 py-2 text-right text-muted-foreground tabular-nums">{money(row.revenue)}</td>
    </>
);

export default function LeadAttributionReport() {
    const { fetch } = useApiMethods();

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState({});
    const [expandedAdsets, setExpandedAdsets] = useState({});

    const [datePreset, setDatePreset] = usePersistedState("ad_attr_preset", "last_90");
    const [customFrom, setCustomFrom] = usePersistedState("ad_attr_from", "");
    const [customTo, setCustomTo] = usePersistedState("ad_attr_to", "");
    const [accountId, setAccountId] = usePersistedState("ad_attr_account", "");
    const [statusFilter, setStatusFilter] = usePersistedState("ad_attr_status", "");

    const load = useCallback(() => {
        if (datePreset === "custom" && (!customFrom || !customTo)) return;
        setLoading(true);
        const { from_date, to_date } = resolveDateRange(datePreset, customFrom, customTo);
        const p = new URLSearchParams();
        if (from_date) p.append("from_date", from_date);
        if (to_date) p.append("to_date", to_date);
        if (accountId) p.append("account_id", accountId);
        if (statusFilter) p.append("status", statusFilter);
        fetch(`${AD_ATTRIBUTION_REPORT_URL}?${p.toString()}`)
            .then((res) => {
                if (res?.status === "success") setData(res);
                else ToastMsg("error", res?.message || "Failed to load report");
            })
            .catch(() => ToastMsg("error", "Failed to load report"))
            .finally(() => setLoading(false));
    }, [datePreset, customFrom, customTo, accountId, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    const tree = data?.data || [];
    const s = data?.summary;
    const wired = data?.attribution?.is_wired;

    return (
        <ContainerCard>
            <div className="p-2 space-y-4">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                    <div>
                        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                            <GitBranch className="h-6 w-6 text-primary" />
                            Lead Attribution
                        </h1>
                        <p className="text-sm text-muted-foreground mt-1">
                            Every Meta lead traced back to the exact campaign, ad set and creative that produced it.
                        </p>
                    </div>
                    <Button size="sm" variant="outline" onClick={load} disabled={loading}>
                        <RefreshCw className={cn("h-4 w-4 mr-1.5", loading && "animate-spin")} />
                        Refresh
                    </Button>
                </div>

                <AdFilterBar
                    {...{ datePreset, setDatePreset, customFrom, setCustomFrom, customTo, setCustomTo,
                        accountId, setAccountId, accounts: data?.filters?.accounts || [] }}
                    >
                        <StatusFilterSelect value={statusFilter} onChange={setStatusFilter} />
                </AdFilterBar>

                <AttributionBanner attribution={data?.attribution} />

                {wired && s && (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                        <StatTile tone="leads" label="Attributed leads" value={num(s.leads)}
                            sub={`${s.campaigns} campaigns · ${s.ads} ads`} />
                        <StatTile label="Quality" value={num(s.quality)} />
                        <StatTile label="Demos" value={num(s.demos)} />
                        <StatTile label="Won" value={num(s.won)} sub={`${pct(s.win_rate, 1)} win rate`} />
                        <StatTile tone="spend" label="Spend" value={money(s.spend)}
                            sub={`${money(s.cpl)} per lead`} />
                        <StatTile tone="cost" label="CAC" value={money(s.cac)}
                            sub={`${money(s.revenue)} revenue`} />
                    </div>
                )}

                {loading && (
                    <div className="flex items-center justify-center py-16 text-muted-foreground">
                        <Loader2 className="h-6 w-6 animate-spin" />
                    </div>
                )}

                {!loading && wired && tree.length === 0 && (
                    <EmptyState title="No attributed leads in this period"
                        hint="Leads carry ad ids, but none were created in this date range. Try widening it." />
                )}

                {!loading && wired && tree.length > 0 && (
                    <div className="overflow-x-auto rounded-lg border border-border">
                        <table className="w-full text-xs">
                            <thead className="bg-muted/50">
                                <tr className="text-muted-foreground">
                                    <th className="px-2 py-2.5 text-left font-medium min-w-[240px]">Campaign / Ad Set / Ad</th>
                                    <th className="px-2 py-2.5 text-right font-medium">Leads</th>
                                    <th className="px-2 py-2.5 text-right font-medium">Quality</th>
                                    <th className="px-2 py-2.5 text-right font-medium">Demos</th>
                                    <th className="px-2 py-2.5 text-right font-medium">Won</th>
                                    <th className="px-2 py-2.5 text-right font-medium">Win %</th>
                                    <th className="px-2 py-2.5 text-left font-medium">Lead → Won</th>
                                    <th className="px-2 py-2.5 text-right font-medium">Spend</th>
                                    <th className="px-2 py-2.5 text-right font-medium">CPL</th>
                                    <th className="px-2 py-2.5 text-right font-medium">Revenue</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {tree.map((campaign) => (
                                    <Fragment key={campaign.id}>
                                        <tr className={cn("bg-muted/20 hover:bg-accent/40 cursor-pointer font-medium",
                                            !isDelivering(campaign.effective_status) && "opacity-60")}
                                            onClick={() => setExpanded((p) => ({ ...p, [campaign.id]: !p[campaign.id] }))}>
                                            <td className="px-2 py-2">
                                                <div className="flex items-center gap-1.5">
                                                    {expanded[campaign.id]
                                                        ? <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                                                        : <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
                                                    <span className="text-foreground truncate max-w-[215px]" title={campaign.name}>
                                                        {campaign.name}
                                                    </span>
                                                    <StatusPill status={campaign.effective_status} compact />
                                                </div>
                                            </td>
                                            <Cells row={campaign} />
                                        </tr>

                                        {expanded[campaign.id] && campaign.adsets.map((adset) => (
                                            <Fragment key={adset.id}>
                                                <tr className={cn("hover:bg-accent/30 cursor-pointer",
                                                    !isDelivering(adset.effective_status) && "opacity-60")}
                                                    onClick={() => setExpandedAdsets((p) => ({ ...p, [adset.id]: !p[adset.id] }))}>
                                                    <td className="px-2 py-2 pl-7">
                                                        <div className="flex items-center gap-1.5">
                                                            {expandedAdsets[adset.id]
                                                                ? <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" />
                                                                : <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground" />}
                                                            <span className="text-foreground truncate max-w-[195px]" title={adset.name}>
                                                                {adset.name}
                                                            </span>
                                                            <StatusPill status={adset.effective_status} compact />
                                                        </div>
                                                    </td>
                                                    <Cells row={adset} />
                                                </tr>

                                                {expandedAdsets[adset.id] && adset.ads.map((ad) => (
                                                    <tr key={ad.id} className={cn("hover:bg-accent/20 bg-background/50",
                                                        !isDelivering(ad.effective_status) && "opacity-60")}>
                                                        <td className="px-2 py-2 pl-14">
                                                            <div className="flex items-center gap-2">
                                                                {ad.thumbnail_url && (
                                                                    <img src={ad.thumbnail_url} alt=""
                                                                        className="h-7 w-7 rounded object-cover shrink-0 border border-border"
                                                                        onError={(e) => { e.target.style.display = "none"; }} />
                                                                )}
                                                                <span className="text-foreground truncate max-w-[145px]" title={ad.name}>
                                                                    {ad.name}
                                                                </span>
                                                                <StatusPill status={ad.effective_status} compact />
                                                                {ad.permalink_url && (
                                                                    <a href={ad.permalink_url} target="_blank" rel="noreferrer"
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        className="text-muted-foreground hover:text-primary shrink-0">
                                                                        <ExternalLink className="h-3 w-3" />
                                                                    </a>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <Cells row={ad} />
                                                    </tr>
                                                ))}
                                            </Fragment>
                                        ))}
                                    </Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </ContainerCard>
    );
}
